<template>
  <!-- Asset Category -->
  <div class="theme-input hrm_input">
    <label class="hrm_input_label text-sm font-medium text-gray-800 dark:text-gray-400">
      {{ trans("lang.labels.asset_category") }}
    </label>
    <div class="relative mt-2">
      <VueMultiselect
        class="hrm-vuemultiselect"
        v-model="filterData[filterItem.assetCategory]"
        :options="assetCategories"
        @select="onChangeAssetCategory"
        @clear="onClearAssetCategory"
        :loading="isLoadingCategory"
        :placeholder="trans('lang.labels.Select')"
        label="category_name"
        valueProp="id"
        :searchable="true"
      />
    </div>
  </div>

  <!-- Asset -->
  <div class="hrm_input">
    <label class="hrm_input_label text-sm font-medium text-gray-800 dark:text-gray-400">
      {{ trans("lang.labels.asset") }}
    </label>
    <div class="relative mt-2">
      <VueMultiselect
        class="hrm-vuemultiselect "
        v-model="filterData[filterItem.asset]"
        :options="assets"
        @select="onChangeAsset"
        @clear="onClearAsset"
        :disabled="!filterData[filterItem.assetCategory]"
        :loading="isLoadingAsset"
        :placeholder="trans('lang.labels.Select')"
        label="name"
        valueProp="id"
        :searchable="true"
      />
    </div>
  </div>

  <!-- Asset Code -->
  <div class="hrm_input">
    <label class="hrm_input_label text-sm font-medium text-gray-800 dark:text-gray-400">
      {{ trans("lang.labels.asset_code") }}
    </label>
    <div class="relative mt-2">
      <VueMultiselect
        class="hrm-vuemultiselect"
        v-model="filterData[filterItem.assetCode]"
        :options="assetCodes"
        @clear="onClearAssetCode"
        :disabled="!filterData[filterItem.asset]"
        :loading="isLoadingCode"
        :placeholder="trans('lang.labels.Select')"
        label="code"
        valueProp="id"
        :searchable="true"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import VueMultiselect from "@vueform/multiselect";
import { trans } from "@/helpers";
import axios from "axios";

const props = defineProps({
  filterItem: { type: Object, required: true },
  filterData: { type: Object, required: true },
});

const assetCategories = ref([]);
const assets = ref([]);
const assetCodes = ref([]);

// loading states for UX
const isLoadingCategory = ref(false);
const isLoadingAsset = ref(false);
const isLoadingCode = ref(false);

/* ----------------------
   HANDLERS
---------------------- */
const onChangeAssetCategory = async () => {
  const categoryId = props.filterData[props.filterItem.assetCategory];
  if (!categoryId) return;

  await getAssetsByCategory(categoryId);
  onClearAsset();
  onClearAssetCode();
};

const onChangeAsset = async () => {
  const assetId = props.filterData[props.filterItem.asset];
  if (!assetId) return;

  await getAssetCodesByAsset(assetId);
  onClearAssetCode();
};

const onClearAssetCategory = () => {
  props.filterData[props.filterItem.assetCategory] = null;
  assets.value = [];
  assetCodes.value = [];
  onClearAsset();
  onClearAssetCode();
};

const onClearAsset = () => {
  props.filterData[props.filterItem.asset] = null;
  assetCodes.value = [];
};

const onClearAssetCode = () => {
  props.filterData[props.filterItem.assetCode] = null;
};

/* ----------------------
   API CALLS
---------------------- */
const getAssetCategories = async () => {
  try {
    isLoadingCategory.value = true;
    const response = await axios.get(route("inventory.asset-categories.index"), {
      params: { perPage: "all", status: 1 },
    });
    assetCategories.value = response.data.data;
  } catch (error) {
    console.error("Error fetching asset categories:", error);
  } finally {
    isLoadingCategory.value = false;
  }
};

const getAssetsByCategory = async (categoryId = null) => {
  try {
    isLoadingAsset.value = true;
    const response = await axios.get(route("inventory.assets.index"), {
      params: { perPage: "all", status: 1, assetsCategoryId: categoryId },
    });
    assets.value = response.data.data;
  } catch (error) {
    console.error("Error fetching assets:", error);
  } finally {
    isLoadingAsset.value = false;
  }
};

const getAssetCodesByAsset = async (assetId = null) => {
  try {
    isLoadingCode.value = true;
    const response = await axios.get(route("inventory.asset-codes.index"), {
      params: { perPage: "all", status: 1, asset_id: assetId },
    });
    assetCodes.value = response.data.data;
  } catch (error) {
    console.error("Error fetching asset codes:", error);
  } finally {
    isLoadingCode.value = false;
  }
};

/* ----------------------
   INITIAL LOAD
---------------------- */
onMounted(async () => {
  await getAssetCategories();
});
</script>

<style scoped>
.hrm-vuemultiselect {
  --ms-border-color: #d1d5db;
  --ms-border-radius: 0.5rem;
  --ms-bg: #fff;
}
</style>
